import { useEffect, useState } from "react";
import { FaArrowCircleLeft, FaArrowCircleRight, FaCheck, FaTimes } from "react-icons/fa";
import Column from "../../layout/column/column";
import Row from "../../layout/row/row";
import { Alignments, classFromProperties, getOptionalProps, getUniqId, Paddings, Sizes } from "../../ui-helpers";
import ActionButton from "../action-button/ActionButton";
import MenuItem from "../menu-item/menu-item";
import MenuTitle from "../menu-title/menu-title";
import Menu from "../menu/menu";
import "./pager.css";

/**
 * Generates a page component.
 * @param {ReactDOM.props} props Component properties.
 * @returns Generated component.
 */
const Pager = (props) => {
  // Excluded properties for automatic properties propagation
  const Excluded = ["id", "withMenu", "showMenu", "onPageChange", "navButtons", "pages", "customButtons", "disabled", "hidden", "validateDisabled"].concat(Alignments, Paddings, Sizes);
  // Initialize states
  const [id] = useState(props?.id ? props.id : getUniqId());
  const [page, setPage] = useState(props?.page || 0);
  const [pages, setPages] = useState(props?.pages || []);
  const [validateDisabled, setValidateDisabled] = useState(props?.validateDisabled || false);

  /**
   * Find the closest valid page index (not StudyManagement, CabinetManagement or title)
   * @param {number} targetPage Target page index
   * @returns {number} Valid page index
   */
  const findValidPage = (targetPage) => {
    const currentPages = props?.pages || [];

    // If target page is valid, return it
    if (currentPages[targetPage] && !currentPages[targetPage].isStudyManagement && !currentPages[targetPage].isCabinetManagement && !currentPages[targetPage].isTitle) {
      return targetPage;
    }

    // Search forward for a valid page
    for (let i = targetPage + 1; i < currentPages.length; i++) {
      if (currentPages[i] && !currentPages[i].isStudyManagement && !currentPages[i].isCabinetManagement && !currentPages[i].isTitle) {
        return i;
      }
    }

    // Search backward for a valid page
    for (let i = targetPage - 1; i >= 0; i--) {
      if (currentPages[i] && !currentPages[i].isStudyManagement && !currentPages[i].isCabinetManagement && !currentPages[i].isTitle) {
        return i;
      }
    }

    return 0; // Fallback to first page
  };
  /**
   * Creates left menu component.
   * @returns Left menu component.
   */
  const createMenu = () => {
    // Check if Pager has withMenu capability (global setting)
    if (!props?.withMenu) return <></>;
    
    // Check pages
    const pages = props?.pages || [];
    if (!pages.length) return <></>;
    
    // Check if current page allows menu to be shown (page-level setting)
    const currentPageItem = pages[page];
    const showMenu = currentPageItem?.showMenu !== false;
    
    // If current page has showMenu set to false, hide the menu
    if (!showMenu) return <></>;

    // Séparer les éléments StudyManagement et CabinetManagement des autres éléments
    const menuItems = [];
    const studyManagementItems = [];
    const cabinetManagementItems = [];

    pages.forEach((pageItem, index) => {
      if (pageItem.isStudyManagement) {
        studyManagementItems.push(<div key={`STUDY${index}`}>{pageItem.content}</div>);
      } else if (pageItem.isCabinetManagement) {
        cabinetManagementItems.push(<div key={`CABINET${index}`}>{pageItem.content}</div>);
      } else if (pageItem.isTitle) {
        menuItems.push(<MenuTitle key={`TITLE${index}`} title={pageItem.title || ""} subTitle={pageItem.subTitle || ""} disabled={pageItem.disabled} hidden={pageItem.hidden} />);
      } else if (pageItem !== false) {
        menuItems.push(
          <MenuItem
            key={`PAGE${index}`}
            disabled={pageItem.disabled}
            hidden={pageItem.hidden}
            selected={index === page}
            onClick={() => {
              props?.onPageChange?.(index);
              setPage(index);
            }}
          >
            {pageItem.icon}
            <span>{pageItem.label}</span>
          </MenuItem>
        );
      }
    });

    return (
      <Menu style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ flex: "1 1 auto" }}>{menuItems}</div>
        <div style={{ flex: "0 0 auto" }}>
          {studyManagementItems}
          {cabinetManagementItems}
        </div>
      </Menu>
    );
  };

  /**
   * Goes to previous page.
   * @param {ClickEvent} evt Click event.
   * @param {Function} handler Handler associated with previous button.
   */
  const gotoPrevious = (evt, handler) => {
    // Get the previous page
    let previousPage = page;
    for (let i = page - 1; i >= 0; i--) {
      const pageItem = pages[i];
      if (!pageItem.isTitle && !pageItem.isStudyManagement && !pageItem.isCabinetManagement && !pageItem.disabled && !pageItem.hidden) {
        previousPage = i;
        break;
      }
    }

    // Check if we can go to next page
    if (page !== previousPage) {
      props?.onPageChange?.(previousPage);
      setPage(previousPage);
      handler?.(evt);
    }
  };
  /**
   * Goes to next page.
   * @param {ClickEvent} evt Click event.
   * @param {Function} handler Handler associated with next button.
   */
  const gotoNext = (evt, handler) => {
    // Get next page
    let nextPage = page;
    for (let i = page + 1; i < pages.length; i++) {
      const pageItem = pages[i];
      if (!pageItem.isTitle && !pageItem.isStudyManagement && !pageItem.isCabinetManagement && !pageItem.disabled && !pageItem.hidden) {
        nextPage = i;
        break;
      }
    }

    // Check if we can go to next page
    if (page !== nextPage) {
      props?.onPageChange?.(nextPage);
      setPage(nextPage);
      handler?.(evt);
    }
  };
  /**
   * Creates a buttpon.
   * @param {Object} button Button parameters.
   * @param {boolean} disabled Defines whether button is disabled.
   * @param {boolean} iconBefore Defines whether button icon is before label.
   * @param {Component} defaultIcon Default icon if none given in parameters.
   * @param {String} defaultLabel Default label if none given in parameters.
   * @param {Function} clickWrapper Optional click handler.
   * @returns Generated button.
   */ const createButton = (button, disabled, iconBefore, defaultIcon, defaultLabel, clickWrapper = null, buttonId = null) => {
    // Extract the icon component from JSX if needed
    const getIconComponent = (iconElement) => {
      if (!iconElement) return null;
      // If it's a JSX element, extract the type (component)
      if (iconElement.type) return iconElement.type;
      // If it's already a component, return it
      return iconElement;
    }; // Determine decorative icon based on button label or default label
    const getDecorativeIcon = (label) => {
      const buttonText = label || defaultLabel;
      switch (buttonText) {
        case "Annuler":
          return { type: "cancel" };
        case "Précédent":
          return { type: "previous" };
        case "Suivant":
          return { type: "next" };
        case "Valider":
          return { type: "validate" };
        default:
          return null;
      }
    };

    // Determine colors based on button label or default label
    const getButtonColors = (label) => {
      const buttonText = label || defaultLabel;
      switch (buttonText) {
        case "Annuler":
          return {
            primary: "#fc466b",
            secondary: "#e73c5e",
            shadow: "rgba(252, 70, 107, 0.4)",
            shadowHover: "rgba(252, 70, 107, 0.6)",
          };
        case "Précédent":
        case "Suivant":
          return {
            primary: "#3b82f6",
            secondary: "#1d4ed8",
            shadow: "rgba(59, 130, 246, 0.4)",
            shadowHover: "rgba(59, 130, 246, 0.6)",
          };
        case "Valider":
          return {
            primary: "#11998e",
            secondary: "#0d7377",
            shadow: "rgba(17, 153, 142, 0.4)",
            shadowHover: "rgba(17, 153, 142, 0.6)",
          };
        default:
          return {
            primary: "#3b82f6",
            secondary: "#1d4ed8",
            shadow: "rgba(59, 130, 246, 0.4)",
            shadowHover: "rgba(59, 130, 246, 0.6)",
          };
      }
    };

    return (
      <ActionButton
        id={buttonId}
        style={{ marginLeft: "5px" }}
        onClick={
          clickWrapper
            ? (evt) => {
                clickWrapper(evt, button.onClick);
              }
            : button.onClick
        }
        colors={getButtonColors(button.label)}
        disabled={disabled}
        icon={getIconComponent(button.icon || defaultIcon)}
        title={button.label ? button.label : defaultLabel}
        subtitle=""
        decorativeIcon={getDecorativeIcon(button.label)}
      />
    );
  };

  /**
   * Creates the buttons bar.
   * @returns Buttons bar component.
   */
  const createButtons = () => {
    // Get buttons
    const buttons = props?.navButtons || {};
    if (!props?.customButtons && !Object.keys(buttons).length) return <></>; // Check if previous is possible
    let previousEnabled = false;
    for (let i = page - 1; i >= 0; i--) {
      const pageItem = pages[i];
      if (!pageItem.isTitle && !pageItem.isStudyManagement && !pageItem.isCabinetManagement && !pageItem.disabled && !pageItem.hidden) {
        previousEnabled = true;
        break;
      }
    }

    // Check if next is possible
    let nextEnabled = false;
    for (let i = page + 1; i < pages.length; i++) {
      const pageItem = pages[i];
      if (!pageItem.isTitle && !pageItem.isStudyManagement && !pageItem.isCabinetManagement && !pageItem.disabled && !pageItem.hidden) {
        nextEnabled = true;
        break;
      }
    }

    return (
      <Row className="pager-buttons">
        <Column className="pager-buttons-left">
          <Row fill>
            {buttons["cancel"] ? createButton(buttons["cancel"], false, true, <FaTimes />, "Annuler") : <></>}
            {buttons["previous"] ? createButton(buttons["previous"], !previousEnabled, true, <FaArrowCircleLeft />, "Précédent", gotoPrevious) : <></>}
          </Row>
        </Column>
        <Column className="pager-buttons-center" fill center>
          {props?.customButtons}
        </Column>
        <Column className="pager-buttons-right">
          <Row fill>
            {buttons["next"] ? createButton(buttons["next"], !nextEnabled, false, <FaArrowCircleRight />, "Suivant", gotoNext) : <></>}
            {buttons["validate"] ? createButton(buttons["validate"], validateDisabled, false, <FaCheck />, "Valider", null, id + "_validate") : <></>}
          </Row>
        </Column>
      </Row>
    );
  };

  // On page property change
  useEffect(() => {
    setPage(props?.page || 0);
  }, [props?.page]);

  // On pages change
  useEffect(() => {
    setPages(props?.pages || []);
    if (page >= props.pages.length) setPage(props.pages.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.pages]);

  // On validate disabled change
  useEffect(() => {
    setValidateDisabled(props?.validateDisabled || false);
  }, [props?.validateDisabled]); // Render component
  const validPage = findValidPage(page);
  return (
    <Column fill id={id} className={classFromProperties(props, "pager", ["disabled"])} {...getOptionalProps(props, Excluded)}>
      <Row fill style={{ overflow: "hidden" }}>
        {createMenu()}
        <Column className="pager-content" fill scroll>
          {pages[validPage] ? pages[validPage].content : null}
        </Column>
      </Row>
      {createButtons()}
    </Column>
  );
};

export default Pager;
